import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { CategoryInput, Category } from '../types';
import { apiService } from '../services/api';

const categorySchema = z.object({
  nome: z.string().min(1, 'Nome é obrigatório'),
  cor: z.string().min(1, 'Cor é obrigatória'),
  descricao: z.string().optional(),
});

type CategoryFormData = z.infer<typeof categorySchema>;

interface CategoryFormProps {
  category?: Category;
  onSuccess: () => void;
  onCancel: () => void;
}

const defaultColors = [
  '#1e3a5f', // Astra Dark Blue
  '#4a9eff', // Astra Light Blue
  '#10B981', // Green
  '#F59E0B', // Yellow
  '#EF4444', // Red
  '#8B5CF6', // Purple
  '#F97316', // Orange
  '#06B6D4', // Cyan
  '#84CC16', // Lime
  '#EC4899', // Pink
];

export function CategoryForm({ category, onSuccess, onCancel }: CategoryFormProps) {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<CategoryFormData>({
    resolver: zodResolver(categorySchema),
    defaultValues: {
      nome: category?.nome || '',
      cor: category?.cor || '#1e3a5f',
      descricao: category?.descricao || '',
    },
  });

  const selectedColor = watch('cor');

  const onSubmit = async (data: CategoryFormData) => {
    try {
      const categoryInput: CategoryInput = {
        nome: data.nome,
        cor: data.cor,
        descricao: data.descricao || undefined,
      };

      if (category) {
        await apiService.updateCategory(category.id, categoryInput);
        toast.success('Categoria atualizada com sucesso');
      } else {
        await apiService.createCategory(categoryInput);
        toast.success('Categoria criada com sucesso');
      }

      onSuccess();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao salvar categoria';
      toast.error(errorMessage);
    }
  };

  return (
    <div className="fixed inset-0 bg-[#0f172a]/90 flex items-center justify-center p-4 z-[100] backdrop-blur-md overflow-y-auto">
      <div className="bg-gradient-to-br from-[#1e293b] to-[#0f172a] rounded-[2.5rem] shadow-[0_0_100px_rgba(0,0,0,0.8)] w-full max-w-lg my-auto border border-white/10 flex flex-col relative animate-in fade-in zoom-in duration-300 max-h-[90vh]" role="dialog" aria-labelledby="form-title">
        {/* Decorative elements */}
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-blue-500/50 to-transparent"></div>
        
        {/* Header */}
        <div className="p-8 pb-4 flex-shrink-0">
          <div className="flex flex-col items-center text-center">
            <div className="w-20 h-20 bg-gradient-to-br from-blue-600/20 to-blue-900/20 rounded-3xl mb-4 flex items-center justify-center border border-blue-500/30 shadow-[inset_0_0_20px_rgba(59,130,246,0.1)] group transition-all duration-500 hover:border-blue-500/50">
              <svg className="w-10 h-10 text-blue-400 group-hover:scale-110 transition-transform duration-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
              </svg>
            </div>
            <h2 id="form-title" className="text-3xl font-bold text-white font-oswald uppercase tracking-tight">
              {category ? 'Editar Categoria' : 'Nova Categoria'}
            </h2>
            <div className="h-1 w-12 bg-blue-600 rounded-full mt-4"></div>
          </div>
        </div>

        {/* Scrollable Form Content */}
        <div className="flex-1 overflow-y-auto custom-scrollbar px-8 py-4">
          <form id="category-form" onSubmit={handleSubmit(onSubmit)} className="space-y-8">
            <div className="space-y-6">
              {/* Name Input */}
              <div className="space-y-3">
                <label htmlFor="nome" className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.3em] font-oswald flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
                  Nome da Categoria *
                </label>
                <div className="relative group">
                  <input
                    id="nome"
                    type="text"
                    {...register('nome')}
                    className="w-full bg-white/5 border border-white/10 rounded-2xl py-4 px-6 text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all font-oswald font-bold text-[11px] tracking-[0.2em] uppercase"
                    placeholder="EX: CLIENTES VIP"
                    aria-invalid={errors.nome ? 'true' : 'false'}
                  />
                  <div className="absolute bottom-0 left-0 h-[2px] w-0 bg-blue-500 group-focus-within:w-full transition-all duration-500 rounded-full"></div>
                </div>
                {errors.nome && (
                  <p className="text-red-400 text-[10px] font-bold uppercase tracking-widest mt-2 flex items-center gap-2 animate-pulse">
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {errors.nome.message}
                  </p>
                )}
              </div>

              {/* Color Selection */}
              <div className="space-y-4">
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.3em] font-oswald flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
                  Cor da Categoria *
                </label>
                
                <div className="grid grid-cols-5 gap-3 p-4 bg-white/5 rounded-[2rem] border border-white/5 shadow-inner">
                  {defaultColors.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setValue('cor', color)}
                      className={`w-full aspect-square rounded-2xl border-2 transition-all duration-300 hover:scale-110 relative group ${
                        selectedColor === color ? 'border-white ring-4 ring-white/10 scale-105 z-10' : 'border-transparent hover:border-white/30'
                      }`}
                      style={{ backgroundColor: color }}
                      aria-label={`Selecionar cor ${color}`}
                    >
                      {selectedColor === color && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <svg className="w-6 h-6 text-white drop-shadow-md" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      )}
                    </button>
                  ))}
                </div>

                {/* Custom Color Picker */}
                <div className="flex items-center gap-6 p-4 bg-white/5 rounded-[2rem] border border-white/5 hover:bg-white/[0.08] transition-colors group cursor-pointer relative overflow-hidden">
                  <div className="relative z-10">
                    <input
                      id="cor"
                      type="color"
                      {...register('cor')}
                      className="w-14 h-14 bg-transparent border-0 rounded-2xl cursor-pointer p-0 overflow-hidden [&::-webkit-color-swatch-wrapper]:p-0 [&::-webkit-color-swatch]:border-0 [&::-webkit-color-swatch]:rounded-2xl shadow-xl transition-transform group-hover:scale-105"
                    />
                    <div className="absolute inset-0 pointer-events-none border-2 border-white/20 rounded-2xl"></div>
                  </div>
                  <div className="relative z-10">
                    <div className="text-[11px] font-bold text-white uppercase tracking-[0.2em] font-oswald">Cor personalizada</div>
                    <div className="text-[9px] text-gray-500 font-bold uppercase tracking-[0.15em] mt-1">Escolha uma tonalidade única</div>
                  </div>
                  <div className="absolute right-6 opacity-0 group-hover:opacity-100 transition-opacity">
                    <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                  </div>
                </div>
                
                {errors.cor && (
                  <p className="text-red-400 text-[10px] font-bold uppercase tracking-widest mt-2 ml-1 animate-pulse">
                    {errors.cor.message}
                  </p>
                )}
              </div>

              {/* Description Field */}
              <div className="space-y-3">
                <label htmlFor="descricao" className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.3em] font-oswald flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
                  Descrição
                </label>
                <div className="relative group">
                  <textarea
                    id="descricao"
                    {...register('descricao')}
                    rows={3}
                    className="w-full bg-white/5 border border-white/10 rounded-2xl py-4 px-6 text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all font-oswald font-bold text-[11px] tracking-[0.15em] resize-none uppercase"
                    placeholder="DESCREVA O PROPÓSITO DESTA CATEGORIA..."
                  />
                  <div className="absolute bottom-0 left-0 h-[2px] w-0 bg-blue-500 group-focus-within:w-full transition-all duration-500 rounded-full"></div>
                </div>
              </div>
            </div>
          </form>
        </div>

        {/* Footer Actions */}
        <div className="p-8 pt-4 flex gap-4 bg-black/20 backdrop-blur-xl border-t border-white/10 flex-shrink-0">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 px-6 py-4 bg-white/5 text-gray-400 font-bold uppercase tracking-[0.2em] text-[10px] rounded-2xl hover:bg-white/10 transition-all border border-white/5 hover:border-white/10 font-oswald flex items-center justify-center gap-2 group"
          >
            <svg className="w-4 h-4 group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Cancelar
          </button>
          <button
            form="category-form"
            type="submit"
            disabled={isSubmitting}
            className="flex-[2] px-8 py-4 bg-blue-600 text-white font-bold uppercase tracking-[0.2em] text-[10px] rounded-2xl shadow-[0_0_20px_rgba(37,99,235,0.3)] hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 flex items-center justify-center gap-3 border border-blue-500 relative overflow-hidden group/btn font-oswald disabled:opacity-50"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover/btn:animate-shimmer" />
            {isSubmitting ? (
              <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            )}
            <span>{isSubmitting ? 'Salvando...' : 'Salvar Categoria'}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
